import React, { useContext, useEffect, useMemo, useState } from 'react'
import { MappedFilters } from '../../../Portfolio_R/SummaryCharts/components/MappedFilters'
import { useTranslation } from 'react-i18next';
import '../../../../components/Reports/ExtemdedReport/GlobalExtendedReport.scss'
import { TagFilterContext } from '../../../../context/TagFilterContext';

type HeaderExtendedProps = {
    portfolioName?: any;
    filtersApplied?: any;
    creationDate?: any;
    regions?: any;
    countries?: string;
    myPortfolio?: string;
}

const PORTFOLIO_NAME_SESSION_KEY = 'ONE_PAGER_HEADER_PORTFOLIO';
const FILTERS_APPLIED_SESSION_KEY = 'ONE_PAGER_HEADER_FILTERS';
const GENERAL_DATA_SESSION_KEY = 'ONE_PAGER_HEADER_GENERAL_DATA';
const canUseSessionStorage = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const readSessionValue = (key: string) => {
    if (!canUseSessionStorage()) return null;
    return sessionStorage.getItem(key);
};

const readSessionJson = (key: string) => {
    if (!canUseSessionStorage()) return null;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (error) {
        sessionStorage.removeItem(key);
        return null;
    }
};

const isStructuredFilters = (filters: any) =>
    Array.isArray(filters) && filters.length >= 3 && (filters[1] || filters[2]);

const HeaderExtended: React.FC<HeaderExtendedProps> = ({ portfolioName, filtersApplied, creationDate, regions, countries, myPortfolio }) => {
    const { t } = useTranslation();
    const { flattenedOptions } = useContext<any>(TagFilterContext);
    const generalDataFromProps = useMemo(() => {
        const payload: any = {};
        if (creationDate) payload.creationDate = creationDate;
        if (regions) payload.regions = regions;
        if (countries) payload.countries = countries;
        return Object.keys(payload).length ? payload : null;
    }, [creationDate, regions, countries]);
    const [storedPortfolioName, setStoredPortfolioName] = useState(() => portfolioName || readSessionValue(PORTFOLIO_NAME_SESSION_KEY) || '');
    const [storedFiltersApplied, setStoredFiltersApplied] = useState(() => {
        const stored = readSessionJson(FILTERS_APPLIED_SESSION_KEY);
        if (isStructuredFilters(stored)) return stored;
        if (isStructuredFilters(filtersApplied)) return filtersApplied;
        return [];
    });
    const [storedGeneralData, setStoredGeneralData] = useState(() => generalDataFromProps || readSessionJson(GENERAL_DATA_SESSION_KEY));

    useEffect(() => {
        if (!portfolioName || !canUseSessionStorage()) return;
        setStoredPortfolioName(portfolioName);
        sessionStorage.setItem(PORTFOLIO_NAME_SESSION_KEY, portfolioName);
    }, [portfolioName]);

    useEffect(() => {
        if (!canUseSessionStorage() || !isStructuredFilters(filtersApplied)) return;
        setStoredFiltersApplied(filtersApplied);
        try {
            sessionStorage.setItem(FILTERS_APPLIED_SESSION_KEY, JSON.stringify(filtersApplied));
        } catch (error) {
            sessionStorage.removeItem(FILTERS_APPLIED_SESSION_KEY);
        }
    }, [filtersApplied]);

    useEffect(() => {
        if (!storedGeneralData && canUseSessionStorage()) {
            const cached = readSessionJson(GENERAL_DATA_SESSION_KEY);
            if (cached) {
                setStoredGeneralData(cached);
            }
        }
    }, [storedGeneralData]);

    useEffect(() => {
        if (!generalDataFromProps || !canUseSessionStorage()) return;

        setStoredGeneralData(prev => {
            const merged = { ...(prev || {}), ...generalDataFromProps };

            try {
                sessionStorage.setItem(GENERAL_DATA_SESSION_KEY, JSON.stringify(merged));
            } catch (error) {
                sessionStorage.removeItem(GENERAL_DATA_SESSION_KEY);
            }

            return merged;
        });
    }, [generalDataFromProps]);

    const effectivePortfolioName = storedPortfolioName || portfolioName || '';
    const effectiveFilters = storedFiltersApplied || filtersApplied || [];
    const effectiveGeneralData = storedGeneralData || generalDataFromProps || {};
    const effectiveCreationDate = effectiveGeneralData?.creationDate || creationDate || '';
    const effectiveRegions = effectiveGeneralData?.regions || regions || '';
    const effectiveCountries = effectiveGeneralData?.countries || countries || '';

    const getByCategory = (category: string) => {
        if (flattenedOptions && flattenedOptions.length) {
            const matches = flattenedOptions.filter(o => o.category === category).map(o => o.label);
            return matches.length ? matches.join(', ') : null;
        }
        return null;
    }

    const sectorsText = (effectiveFilters && effectiveFilters[1]) || getByCategory('sector') || null;
    const divisionText = (effectiveFilters && effectiveFilters[2]) || getByCategory('division') || null;

    return (
        <header className="c-titled">
            <div className="summary-header">
                <h2>
                    {t('one_pager_report.wallet')} {effectivePortfolioName}
                </h2>

                <p className="region_title">
                    {effectiveRegions && (
                        <>
                            {effectiveRegions === "CAN" ? (
                                <>
                                    <strong>{t('one_pager_report.region')}:</strong> {`${t('one_pager_report.departament-andean-group')} (${effectiveRegions})`}
                                </>
                            ) : (
                                <>
                                    <strong>{t('one_pager_report.region')}:</strong> {effectiveRegions}
                                </>
                            )}
                        </>
                    )}
                    {effectiveCountries && (
                        <>
                            {(effectiveRegions) && (", ")}<strong>{t('one_pager_report.country')}:</strong>{"  "}{effectiveCountries}
                        </>
                    )}
                    {sectorsText && (
                        <>
                            {(effectiveRegions || effectiveCountries) && (", ")}<strong>{t('one_pager_report.sectors')}:</strong>{"  "}{sectorsText}
                        </>
                    )}
                    {divisionText && (
                        <>
                            {(effectiveRegions || effectiveCountries || sectorsText) && (", ")}<strong>{t('one_pager_report.division')}:</strong>{"  "}{divisionText}
                        </>
                    )}
                </p>
            </div>
            <div className="summary-header text-align-right">
                <p className='right-title-report'>
                    {t('extended_report.extended_report_header')}{" "}
                    <span className='suptitled_report'>{t('app_name').toUpperCase()}</span>
                </p>
                <p className='creation-date'>
                    {t('one_pager_report.creation')}: {effectiveCreationDate}
                </p>
            </div>
        </header>
    )
}

export default HeaderExtended
